import { availableShippingCountryOptions } from '../LocationForm/ShippingRateModalLocationForm.constants';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { CustomShippingRateModalPages } from '@/components/ShippingInfo/LiveShippingInfo/components/ShippingPickupOptions/components/LiveShipping/components/CustomShippingRateModal/hooks/useCustomShippingRateModalPages';
import {
    getBidderEmail,
    getDefaultAddressCountryCode,
    getDefaultAddressPostalCode,
} from '@/redux/modules/account/user/user.selectors';
import { getItemModelLotNumber, getItemModelTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { LIVE_SHIPPING_METHOD_TRANSLATION_KEY } from '@/components/ShippingInfo/LiveShippingInfo/components/ShippingPickupOptions/components/LiveShipping/LiveShippingMethodRow.constants';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import styled, { css } from 'styled-components';

export type CustomShippingRateFormProps = {
    itemId: number;
    onSubmit: (postalCode: string, email: string, country: string) => Promise<void>;
    providedCountryCode: string;
    providedPostalCode: string;
};

const CustomShippingRateForm = ({
    itemId,
    onSubmit,
    providedCountryCode,
    providedPostalCode,
}: CustomShippingRateFormProps) => {
    const { formatMessage } = useIntl();

    const [isLoading, setIsLoading] = useState(false);

    const title = useAppSelector((state) => getItemModelTitle(state, itemId));
    const lotNumber = useAppSelector((state) => getItemModelLotNumber(state, itemId));

    const loggedInUserPostalCode = useAppSelector(getDefaultAddressPostalCode);
    const loggedInUserEmail = useAppSelector(getBidderEmail);

    const [postalCode, setPostalCode] = useState(providedPostalCode || loggedInUserPostalCode);
    const [email, setEmail] = useState(loggedInUserEmail);

    const loggedInUserCountryCode = useAppSelector(getDefaultAddressCountryCode);

    const [country, setSelectedCountry] = useState(
        availableShippingCountryOptions.find(({ value }) => value === providedCountryCode) ??
            availableShippingCountryOptions.find(({ value }) => value === loggedInUserCountryCode) ??
            availableShippingCountryOptions.find(({ value }) => value === 'US')
    );

    const onChangeCountry = useCallback((newValue: OptionType) => {
        let country = newValue.value ?? newValue;
        setSelectedCountry(country);
    }, []);

    useEffect(() => {
        if (isLoading) {
            const inputCountry = country.value ?? country;
            onSubmit(postalCode, email, inputCountry).then(() => {
                setIsLoading(false);
            });
        }
    }, [email, isLoading, onSubmit, postalCode, country]);

    return (
        <StyledRateForm>
            {/* Item info */}
            <div>
                <div className="hui-text-label-mini">
                    {formatMessage({
                        id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.${CustomShippingRateModalPages.TooExpensiveForm}.fields.item.label`,
                    })}
                </div>
                <div className="hui-text-body-primary">
                    {formatMessage(
                        {
                            id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.${CustomShippingRateModalPages.TooExpensiveForm}.fields.item.name`,
                        },
                        {
                            lotNumber,
                            title,
                        }
                    )}
                </div>
            </div>
            <StyledSelect
                data-testid="custom-shipping-rate-form.country-select"
                defaultValue={country}
                labelText={formatMessage({
                    id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.${CustomShippingRateModalPages.TooExpensiveForm}.fields.countrySelect.label`,
                })}
                onChange={onChangeCountry}
                options={availableShippingCountryOptions}
                usePortalMenu
            />
            {/* Postal Code */}
            <TextInput
                defaultValue={postalCode}
                labelText={formatMessage({
                    id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.${CustomShippingRateModalPages.TooExpensiveForm}.fields.postalCode.label`,
                })}
                onChange={(event) => {
                    setPostalCode(event.currentTarget.value);
                }}
            />
            {/* Email */}
            <TextInput
                defaultValue={email}
                labelText={formatMessage({
                    id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.${CustomShippingRateModalPages.TooExpensiveForm}.fields.email.label`,
                })}
                onChange={(event) => {
                    setEmail(event.currentTarget.value);
                }}
            />
            {/* Send email button */}
            <Button
                disabled={!Boolean(email && postalCode)}
                loading={isLoading}
                onClick={() => {
                    setIsLoading(true);
                }}
            >
                {formatMessage({
                    id: `${LIVE_SHIPPING_METHOD_TRANSLATION_KEY}.customShippingRateModal.${CustomShippingRateModalPages.TooExpensiveForm}.request`,
                })}
            </Button>
        </StyledRateForm>
    );
};

export default CustomShippingRateForm;

const StyledSelect = styled(Select)`
    width: 375px;
    ${({ theme }) => css`
        @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
            width: 280px;
        }
    `}
`;

const StyledRateForm = styled.section`
    & > :not(:last-child) {
        margin-bottom: 24px;
    }

    & > :last-child {
        margin: 32px auto 0 auto;
        ${({ theme }) => css`
            @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
                width: 100%;
            }
        `}
    }
`;
